import { call, put, takeEvery, all, select } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import axios from 'axios';
import _ from "../shared/utils/ObjectUtils";
import EntityUtils from "./utils/EntityUtils";
import { availableEntityTypes } from '../../smartBuildings/otherEntities/utils';
import { translateError } from '../shared/utils/Errors';
import { getUserPageRefresh } from '../auth/sagas';

const controllerUrl = '/entities/';

const searchEntitiesCall = searchFields => new Promise((resolve, reject) => {
        axios.get(controllerUrl + 'search', {
            params: {
                ...searchFields,
            }
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
});

const getEntityListCall = (filter = {}) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'list', {
        params: filter
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const getEntityCall = id => new Promise((resolve, reject) => {
    axios.get(controllerUrl + id).then(
        response => resolve(response.data),
        () => resolve({ id, name: '- - -' })
    );
});

const createEntityCall = entityData => new Promise((resolve, reject) => {
    axios.post(controllerUrl, entityData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const editEntityCall = (entityId, entityData) => new Promise((resolve, reject) => {
    axios.put(controllerUrl + entityId, entityData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const deleteEntityCall = entityId => new Promise((resolve, reject) => {
    axios.delete(controllerUrl + entityId).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

function* getEntityListRequest({ filter }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const entityList = yield call(() => getEntityListCall(filter));

        yield put({
            type: actionTypes.GET_ENTITY_LIST_SUCCESS,
            payload: entityList.map(entity => ({ value: entity.id, label: entity.parent ? entity.parent.name + " | " + entity.name : entity.name }))
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_ENTITY_LIST_FAILED
        });
    }
}

function* searchEntitiesRequest({ searchFields }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const searchResults = yield call(() => searchEntitiesCall(searchFields));
        const parentIds = searchResults.content.map(entity => entity.parentId);
        const uniqueParentIds = [...new Set(parentIds)];

        const parentEntities = yield all(
            uniqueParentIds
                .filter(id => !_.isNil(id))
                .map(id => call(getEntityCall, id))
        );
        const searchResultsWithParents = {
            ...searchResults,
            content: searchResults.content.map(
                entity => ({
                    ...entity,
                    parent: parentEntities.find(e => e.id === entity.parentId)
                })
            )
        };
        yield put({
            type: actionTypes.ENTITY_SEARCH_SUCCESS,
            payload: searchResultsWithParents
        });
    } catch (e) {
        yield put({
            type: actionTypes.ENTITY_SEARCH_FAILED
        });
    }
}

function* getEntityRequest({ entityId }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const entity = yield call(getEntityCall, entityId);

        entity.parent = entity.parentId;
        if (entity.imageLink) {
            entity.imageLink = "/static/files/" + entity.imageLink;
        }

        const entityFEObject = EntityUtils.spreadEntityTypesSpecificData(
            availableEntityTypes.building, 
            entity
        );

        yield put({
            type: actionTypes.GET_ENTITY_SUCCESS,
            payload: entityFEObject
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_ENTITY_FAILED
        });
    }
}

function* createEntityRequest({ entityData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        entityData.parentId = entityData.parent;
        entityData.providerId = entityData.provider;

        entityData = EntityUtils.addEntityTypesSpecificData(entityData.type, entityData);
        entityData = EntityUtils.buildMultipartRequest(entityData);

        const entity = yield call(createEntityCall, entityData);

        yield put({
            type: actionTypes.CREATE_ENTITY_SUCCESS,
            payload: entity
        });
    } catch ({ response }) {
        yield put({
            type: actionTypes.CREATE_ENTITY_FAILED,
            payload: translateError(getEntityTypeErrorKey(entityData.type), response.status, response.data.status)
        });
    }
}

function* editEntityRequest({ entityId, entityData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        entityData.parentId = entityData.parent;
        entityData.providerId = entityData.provider;

        entityData = EntityUtils.addEntityTypesSpecificData(entityData.type, entityData);
        entityData = EntityUtils.buildMultipartRequest(entityData);

        const entity = yield call(editEntityCall, entityId, entityData);

        yield put({
            type: actionTypes.EDIT_ENTITY_SUCCESS,
            payload: entity
        });
    } catch ({ response }) {
        yield put({
            type: actionTypes.EDIT_ENTITY_FAILED,
            payload: translateError(getEntityTypeErrorKey(entityData.type), response.status, response.data.status)
        });
    }
}

function* deleteEntityRequest({ entityId }) {
    try {
        yield call(deleteEntityCall, entityId);

        yield put({
            type: actionTypes.DELETE_ENTITY_SUCCESS
        });
    } catch (e) {
        yield put({
            type: actionTypes.DELETE_ENTITY_FAILED
        });
    }
}

const entitySagas = [
    takeEvery(actionTypes.GET_ENTITY_LIST_REQUEST, getEntityListRequest),
    takeEvery(actionTypes.ENTITY_SEARCH_REQUEST, searchEntitiesRequest),
    takeEvery(actionTypes.GET_ENTITY_REQUEST, getEntityRequest),
    takeEvery(actionTypes.CREATE_ENTITY_REQUEST, createEntityRequest),
    takeEvery(actionTypes.EDIT_ENTITY_REQUEST, editEntityRequest),
    takeEvery(actionTypes.DELETE_ENTITY_REQUEST, deleteEntityRequest),
];

function getEntityTypeErrorKey(entityType) {
    let result = '';
    switch (entityType) {
        case availableEntityTypes.meetingRoom:
            result = 'meetingRooms';
            break;
        case availableEntityTypes.building:
            result = 'buildings';
            break;
        default:
            result = 'entities';
            break;
    }
    return result;
}

export {
    entitySagas,
    getEntityCall,
    createEntityCall,
    editEntityCall,
    deleteEntityCall,
    getEntityListCall
};